/**
 * Stores per-video progress in localStorage as:
 * {
 *   "dQw4w9WgXcQ": { "progress": 42, "completed": false },
 *   "9bZkp7q19f0": { "progress": 0, "completed": true }
 * }
 *
 * `progress` is in seconds.
 */

export const LS_VIDEO_PROGRESS = 'ARISE_VIDEO_PROGRESS';
type VideoProgressMap = Record<string, { progress: number; completed: boolean }>;

export function saveLocalVideoProgress(videoId: string, seconds: number): void {
    try {
        if (!videoId || seconds < 0) return;
        const map = getLSMap();

        // No need to save progress if completed
        if (map[videoId]?.completed === true) return;
        map[videoId] = { progress: seconds, completed: false };

        localStorage.setItem(LS_VIDEO_PROGRESS, JSON.stringify(map));
    } catch (error) {
        console.log('Warning: Could not save local video progress', error);
    }
}

export function markVideoCompleted(videoId: string): void {
    try {
        if (!videoId) return;

        const map = getLSMap();
        map[videoId] = { progress: 0, completed: true };

        localStorage.setItem(LS_VIDEO_PROGRESS, JSON.stringify(map));
    } catch (error) {
        console.log('Warning: Could not mark video as completed', error);
    }
}

export function getLocalVideoProgress(videoId: string): { progress: number; completed: boolean } {
    try {
        const map = getLSMap();
        const entry = map[videoId];

        if (typeof entry?.progress === 'number' && typeof entry?.completed === 'boolean') {
            return entry;
        }
        return { progress: 0, completed: false };
    } catch (error) {
        console.log('Warning: Could not get local video progress, defaulting to 0', error);
        return { progress: 0, completed: false };
    }
}

function getLSMap(): VideoProgressMap {
    const raw = localStorage.getItem(LS_VIDEO_PROGRESS);
    return raw ? JSON.parse(raw) : {};
}

export function clearLocalVideoProgressFromStorage() {
    localStorage.removeItem(LS_VIDEO_PROGRESS);
}
